/**
 * SectionMFactorsCard — v3.18.0 (Strategy Stage, Phase 1)
 *
 * Captures Section M Evaluation Factors. Supports AI-extract from the
 * RFP via the existing Shredder pipeline; user then reviews / edits /
 * confirms before saving. Bulk PUT replaces the full factor list.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Skeleton } from '../ui/skeleton';
import {
  Plus, Trash2, Sparkles, Save, Loader2, AlertCircle, ChevronDown, ChevronRight,
  FileSearch2, Scale, FileStack,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function emptyFactor() {
  return { label: '', weight: null, notes: '', sub_factors: [] };
}

function emptySubFactor() {
  return { label: '', weight: null, notes: '' };
}

function emptyVolume() {
  return { label: '', page_limit: '', format_notes: '', notes: '' };
}

export default function SectionMFactorsCard({ pursuitId }) {
  const { getAuthHeaders } = useAuth();
  const [factors, setFactors] = useState(null);
  const [serverCount, setServerCount] = useState(0);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [dirty, setDirty] = useState(false);
  const [aiConfidence, setAiConfidence] = useState(null);
  // v3.18.17 — Preserve manual edits on Section M re-extract (default ON).
  const [preserveEdits, setPreserveEdits] = useState(true);

  // Submittal Volumes (Section L) — Table 1, above Evaluation Factors.
  const [volumes, setVolumes] = useState(null);
  const [volumesDirty, setVolumesDirty] = useState(false);
  const [savingVolumes, setSavingVolumes] = useState(false);

  const fetchFactors = useCallback(async () => {
    if (!pursuitId) return;
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/section-m`,
        { headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setFactors(body.factors || []);
      setServerCount(body.count || 0);
      // Default-expand first AI-extracted factor
      const firstAi = (body.factors || []).find(f => f.extraction_source === 'ai');
      if (firstAi) setExpanded({ [firstAi.id]: true });
    } catch (e) {
      console.error('section-m fetch failed', e);
      setFactors([]);
    }
  }, [pursuitId, getAuthHeaders]);

  const fetchVolumes = useCallback(async () => {
    if (!pursuitId) return;
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/submittal-volumes`,
        { headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setVolumes(body.volumes || []);
    } catch (e) {
      console.error('submittal-volumes fetch failed', e);
      setVolumes([]);
    }
  }, [pursuitId, getAuthHeaders]);

  useEffect(() => { fetchFactors(); fetchVolumes(); }, [fetchFactors, fetchVolumes]);

  const runExtract = async () => {
    if (extracting) return;
    setExtracting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/section-m/extract?preserve_edits=${preserveEdits ? 'true' : 'false'}`,
        { method: 'POST', headers: getAuthHeaders() },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${res.status}`);
      }
      const body = await res.json();
      const preservedNote = body.preserved_count > 0 ? ` (kept ${body.preserved_count} of your edits)` : '';
      toast.success(
        `Extracted ${body.volumes_count || 0} submittal volume${(body.volumes_count === 1) ? '' : 's'} `
        + `+ ${body.extracted_count} eval factor${body.extracted_count === 1 ? '' : 's'}${preservedNote}`,
      );
      setAiConfidence(body.confidence);
      await Promise.all([fetchFactors(), fetchVolumes()]);
      setDirty(false);
      setVolumesDirty(false);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'AI extraction failed');
    } finally {
      setExtracting(false);
    }
  };

  // --- Submittal Volume handlers ------------------------------------------
  const saveVolumes = async () => {
    setSavingVolumes(true);
    try {
      const cleaned = (volumes || [])
        .map(v => ({
          label: (v.label || '').trim(),
          page_limit: (v.page_limit || '').trim(),
          format_notes: (v.format_notes || '').trim(),
          notes: (v.notes || '').trim(),
        }))
        .filter(v => v.label);
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/submittal-volumes`,
        {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ volumes: cleaned }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Submittal volumes saved');
      await fetchVolumes();
      setVolumesDirty(false);
    } catch (e) {
      console.error(e);
      toast.error('Failed to save volumes');
    } finally {
      setSavingVolumes(false);
    }
  };

  const updateVolume = (idx, patch) => {
    const next = [...(volumes || [])];
    next[idx] = { ...next[idx], ...patch };
    setVolumes(next);
    setVolumesDirty(true);
  };
  const addVolume = () => {
    setVolumes([...(volumes || []), { ...emptyVolume(), id: `local-${Date.now()}` }]);
    setVolumesDirty(true);
  };
  const removeVolume = (idx) => {
    const next = [...(volumes || [])];
    next.splice(idx, 1);
    setVolumes(next);
    setVolumesDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleaned = (factors || []).map(f => ({
        label: f.label,
        weight: f.weight === '' || f.weight === undefined ? null : Number(f.weight),
        notes: f.notes || '',
        sub_factors: (f.sub_factors || []).map(s => ({
          label: s.label,
          weight: s.weight === '' || s.weight === undefined ? null : Number(s.weight),
          notes: s.notes || '',
        })).filter(s => s.label?.trim()),
      })).filter(f => f.label?.trim());

      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/section-m`,
        {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ factors: cleaned }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Section M saved');
      await fetchFactors();
      setDirty(false);
    } catch (e) {
      console.error(e);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateFactor = (idx, patch) => {
    const next = [...(factors || [])];
    next[idx] = { ...next[idx], ...patch };
    setFactors(next);
    setDirty(true);
  };

  const updateSubFactor = (fIdx, sIdx, patch) => {
    const next = [...(factors || [])];
    const subs = [...(next[fIdx].sub_factors || [])];
    subs[sIdx] = { ...subs[sIdx], ...patch };
    next[fIdx] = { ...next[fIdx], sub_factors: subs };
    setFactors(next);
    setDirty(true);
  };

  const addFactor = () => {
    setFactors([...(factors || []), { ...emptyFactor(), id: `local-${Date.now()}` }]);
    setDirty(true);
  };

  const removeFactor = (idx) => {
    const next = [...(factors || [])];
    next.splice(idx, 1);
    setFactors(next);
    setDirty(true);
  };

  const addSubFactor = (fIdx) => {
    const next = [...(factors || [])];
    next[fIdx] = {
      ...next[fIdx],
      sub_factors: [...(next[fIdx].sub_factors || []), { ...emptySubFactor(), _local: Date.now() }],
    };
    setFactors(next);
    setDirty(true);
  };

  const removeSubFactor = (fIdx, sIdx) => {
    const next = [...(factors || [])];
    const subs = [...(next[fIdx].sub_factors || [])];
    subs.splice(sIdx, 1);
    next[fIdx] = { ...next[fIdx], sub_factors: subs };
    setFactors(next);
    setDirty(true);
  };

  if (factors === null) {
    return <Skeleton className="h-40 w-full" />;
  }

  const weightSum = factors.reduce((acc, f) => acc + (Number(f.weight) || 0), 0);
  const weightBalanced = Math.abs(weightSum - 100) < 0.5;
  const anyAi = factors.some(f => f.extraction_source === 'ai' || f.extraction_source === 'edited_ai');

  return (
    <Card className="border-cyan-100 shadow-sm" data-testid="section-m-factors-card">
      <CardContent className="p-4 space-y-3">
        {/* AI extract toolbar */}
        <div className="flex flex-wrap items-center gap-3 p-3 bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-100 rounded-lg">
          <Sparkles className="w-5 h-5 text-cyan-700 flex-shrink-0" />
          <div className="flex-1 min-w-[240px]">
            <p className="text-sm font-semibold text-slate-900">AI Submittal &amp; Evaluation Extractor</p>
            <p className="text-xs text-slate-600">
              Reads the uploaded RFP and proposes Submittal Volumes (Section L) and Evaluation Factors (Section M). You confirm or edit before save.
            </p>
          </div>
          {aiConfidence !== null && aiConfidence !== undefined && (
            <Badge variant="outline" className="bg-white border-cyan-200 text-cyan-800">
              AI confidence: {Math.round(Number(aiConfidence) * 100)}%
            </Badge>
          )}
          {/* v3.18.17 — Preserve-edits toggle. ON by default so re-extract
              doesn't blow away factors the user has hand-edited. */}
          {factors.length > 0 && (
            <label
              className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer select-none"
              title="When ON, factors you have manually edited are kept and only AI-untouched rows are replaced."
              data-testid="section-m-preserve-edits-toggle-wrap"
            >
              <input
                type="checkbox"
                checked={preserveEdits}
                onChange={(e) => setPreserveEdits(e.target.checked)}
                disabled={extracting}
                className="w-3.5 h-3.5 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500 cursor-pointer"
                data-testid="section-m-preserve-edits-toggle"
              />
              <span className="font-medium">Preserve my edits</span>
            </label>
          )}
          <Button
            onClick={runExtract}
            disabled={extracting}
            className="bg-cyan-700 hover:bg-cyan-800 text-white"
            data-testid="section-m-ai-extract-btn"
          >
            {extracting ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Extracting…
              </>
            ) : (
              <>
                <FileSearch2 className="w-4 h-4 mr-1" />
                {factors.length > 0 ? 'Re-extract from RFP' : 'Extract from RFP'}
              </>
            )}
          </Button>
        </div>

        {/* ── TABLE 1: Submittal Volumes (Section L) ───────────────────── */}
        <div className="space-y-2" data-testid="submittal-volumes-section">
          <div className="flex items-center gap-2">
            <FileStack className="w-4 h-4 text-indigo-600" />
            <h3 className="text-sm font-semibold text-slate-900">Submittal Volumes</h3>
            <span className="text-[11px] text-slate-400">Section L · what the offeror must submit</span>
          </div>

          {(volumes || []).length === 0 ? (
            <div className="text-center py-5 px-4 border-2 border-dashed border-indigo-200 rounded-lg bg-indigo-50/30">
              <p className="text-xs text-slate-600">
                No submittal volumes captured yet. Run the extractor above or add manually.
              </p>
              <Button variant="outline" size="sm" onClick={addVolume} className="mt-2"
                data-testid="submittal-add-manual-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add volume manually
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Column hints */}
              <div className="grid gap-2 px-2 text-[9px] font-semibold uppercase tracking-wider text-slate-400"
                style={{ gridTemplateColumns: '1.6fr 0.9fr 2fr auto' }}>
                <div>Volume</div>
                <div>Page Limit</div>
                <div>Format / Submission Notes</div>
                <div />
              </div>
              {(volumes || []).map((v, vIdx) => (
                <div key={v.id || vIdx}
                  className="grid gap-2 items-start p-2 bg-white border border-slate-200 rounded-lg"
                  style={{ gridTemplateColumns: '1.6fr 0.9fr 2fr auto' }}
                  data-testid={`submittal-volume-${vIdx}`}>
                  <Input
                    value={v.label}
                    onChange={(e) => updateVolume(vIdx, { label: e.target.value })}
                    placeholder="Volume I — Technical"
                    className="h-8 text-xs"
                    data-testid={`submittal-volume-label-${vIdx}`}
                  />
                  <Input
                    value={v.page_limit || ''}
                    onChange={(e) => updateVolume(vIdx, { page_limit: e.target.value })}
                    placeholder="25 pages"
                    className="h-8 text-xs"
                  />
                  <Input
                    value={v.format_notes || ''}
                    onChange={(e) => updateVolume(vIdx, { format_notes: e.target.value })}
                    placeholder="Font, margins, file format, submission method…"
                    className="h-8 text-xs"
                  />
                  <Button size="icon" variant="ghost"
                    className="h-8 w-8 text-red-600 hover:bg-red-50"
                    onClick={() => removeVolume(vIdx)} title="Remove volume"
                    data-testid={`submittal-volume-delete-${vIdx}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              <div className="flex flex-wrap gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={addVolume}
                  className="border-dashed border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                  data-testid="submittal-add-volume-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add volume
                </Button>
                <Button size="sm" onClick={saveVolumes} disabled={savingVolumes || !volumesDirty}
                  className="ml-auto bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="submittal-save-btn">
                  {savingVolumes
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Saving…</>
                    : <><Save className="w-3.5 h-3.5 mr-1" /> {volumesDirty ? 'Save volumes' : 'Saved'}</>}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Divider between the two tables */}
        <div className="border-t border-slate-200 pt-2">
          <div className="flex items-center gap-2 mb-1">
            <Scale className="w-4 h-4 text-cyan-600" />
            <h3 className="text-sm font-semibold text-slate-900">Evaluation Factors</h3>
            <span className="text-[11px] text-slate-400">Section M · how the Government scores &amp; selects</span>
          </div>
        </div>

        {anyAi && (
          <div className="flex items-start gap-2 text-xs text-slate-600 px-1">
            <AlertCircle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
            <span>
              Factors marked <Badge variant="outline" className="text-[10px] py-0 px-1 bg-cyan-50 border-cyan-200 text-cyan-800">AI</Badge> were proposed by the extractor. Review and confirm — the system saves your edits separately so re-extracting won't blow them away unintentionally.
            </span>
          </div>
        )}

        {/* Weight summary */}
        {factors.length > 0 && (
          <div className="flex items-center justify-between p-2 bg-slate-50 rounded-md text-xs">
            <div className="flex items-center gap-2 text-slate-700">
              <Scale className="w-3.5 h-3.5" />
              Total weight across factors:
              <span
                className={cn(
                  'font-bold tabular-nums',
                  weightBalanced ? 'text-emerald-700' : weightSum > 0 ? 'text-amber-700' : 'text-slate-500',
                )}
                data-testid="section-m-weight-sum"
              >
                {weightSum.toFixed(0)}
              </span>
              <span className="text-slate-400">/ 100</span>
            </div>
            {!weightBalanced && weightSum > 0 && (
              <span className="text-amber-700 italic">Weights should sum to ~100</span>
            )}
          </div>
        )}

        {/* Factors list */}
        {factors.length === 0 ? (
          <div
            className="text-center py-8 px-4 border-2 border-dashed border-cyan-200 rounded-lg bg-cyan-50/30"
            data-testid="section-m-empty"
          >
            <FileSearch2 className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
            <p className="text-sm text-slate-700 font-medium">
              No evaluation factors captured yet.
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Run the AI extractor above or add factors manually.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={addFactor}
              className="mt-3"
              data-testid="section-m-add-manual-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add factor manually
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {factors.map((f, fIdx) => {
              const isOpen = expanded[f.id] !== false; // default open
              const sourceBadge = f.extraction_source === 'ai' ? 'AI' :
                                  f.extraction_source === 'edited_ai' ? 'AI · edited' : null;
              return (
                <div
                  key={f.id}
                  className="border border-slate-200 rounded-lg bg-white"
                  data-testid={`section-m-factor-${fIdx}`}
                >
                  <div className="flex items-start gap-2 p-3">
                    <button
                      type="button"
                      onClick={() => setExpanded({ ...expanded, [f.id]: !isOpen })}
                      className="mt-2 flex-shrink-0 text-slate-400 hover:text-slate-700"
                      data-testid={`section-m-factor-toggle-${fIdx}`}
                    >
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Input
                          value={f.label}
                          onChange={(e) => updateFactor(fIdx, { label: e.target.value })}
                          placeholder="Factor label (e.g., Factor 1 — Technical Approach)"
                          className="flex-1 min-w-[240px]"
                          data-testid={`section-m-factor-label-${fIdx}`}
                        />
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            min={0}
                            max={100}
                            value={f.weight === null || f.weight === undefined ? '' : f.weight}
                            onChange={(e) => updateFactor(fIdx, { weight: e.target.value })}
                            placeholder="Wt"
                            className="w-20"
                            data-testid={`section-m-factor-weight-${fIdx}`}
                          />
                          <span className="text-xs text-slate-500">%</span>
                        </div>
                        {sourceBadge && (
                          <Badge variant="outline" className="text-[10px] py-0 px-1 bg-cyan-50 border-cyan-200 text-cyan-800">
                            {sourceBadge}
                          </Badge>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-red-600 hover:bg-red-50"
                          onClick={() => removeFactor(fIdx)}
                          title="Remove factor"
                          data-testid={`section-m-factor-delete-${fIdx}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      {isOpen && (
                        <>
                          <Textarea
                            value={f.notes || ''}
                            onChange={(e) => updateFactor(fIdx, { notes: e.target.value })}
                            placeholder="Notes — what the evaluator is looking for; key phrases from the RFP…"
                            rows={2}
                            data-testid={`section-m-factor-notes-${fIdx}`}
                          />
                          {/* Sub-factors */}
                          <div className="pl-3 border-l-2 border-slate-200 space-y-2">
                            {(f.sub_factors || []).map((s, sIdx) => (
                              <div
                                key={s.id || s._local || sIdx}
                                className="flex flex-wrap items-start gap-2"
                                data-testid={`section-m-subfactor-${fIdx}-${sIdx}`}
                              >
                                <Input
                                  value={s.label}
                                  onChange={(e) => updateSubFactor(fIdx, sIdx, { label: e.target.value })}
                                  placeholder="Sub-factor label"
                                  className="flex-1 min-w-[200px]"
                                  data-testid={`section-m-subfactor-label-${fIdx}-${sIdx}`}
                                />
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={s.weight === null || s.weight === undefined ? '' : s.weight}
                                    onChange={(e) => updateSubFactor(fIdx, sIdx, { weight: e.target.value })}
                                    placeholder="Wt"
                                    className="w-20"
                                    data-testid={`section-m-subfactor-weight-${fIdx}-${sIdx}`}
                                  />
                                  <span className="text-xs text-slate-500">%</span>
                                </div>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 text-red-600 hover:bg-red-50"
                                  onClick={() => removeSubFactor(fIdx, sIdx)}
                                  title="Remove sub-factor"
                                  data-testid={`section-m-subfactor-delete-${fIdx}-${sIdx}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            ))}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => addSubFactor(fIdx)}
                              className="text-xs text-slate-600"
                              data-testid={`section-m-add-subfactor-${fIdx}`}
                            >
                              <Plus className="w-3 h-3 mr-1" /> Add sub-factor
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                variant="outline"
                onClick={addFactor}
                className="border-dashed border-cyan-200 text-cyan-700 hover:bg-cyan-50"
                data-testid="section-m-add-factor-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add factor
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !dirty}
                className="ml-auto bg-cyan-700 hover:bg-cyan-800 text-white"
                data-testid="section-m-save-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-1" /> {dirty ? 'Save changes' : 'Saved'}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Hidden but useful for tests: server-side count */}
        <span className="hidden" data-testid="section-m-server-count">{serverCount}</span>
      </CardContent>
    </Card>
  );
}
